import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  MapPin, CheckCircle, MessageCircle, ArrowRight,
  BookOpen, Clock, Star, Users
} from 'lucide-react';
import useSEO from '../utils/useSEO';

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i = 0) => ({
    opacity: 1, y: 0,
    transition: { duration: 0.55, delay: i * 0.1, ease: 'easeOut' },
  }),
};

// Config for each location page (assignment + insurance)
const LOCATION_CONFIG = {
  // Assignment location pages
  'assignment-help-mumbai': {
    city: 'Mumbai',
    service: 'Assignment Writing',
    type: 'assignment',
    title: 'Assignment Writing Service in Mumbai | College & Engineering Help | AGO Innovations',
    description:
      'Looking for assignment writing help in Mumbai? AGO Innovations provides expert assignment writing for engineering, medical, and college students across Mumbai. Fast delivery.',
    keywords:
      'assignment writing service Mumbai, assignment help Mumbai, college assignment help Mumbai, engineering assignment help Mumbai, student assignment service Mumbai',
    canonical: 'https://agoinnovations.in/assignment-help-mumbai',
    areas: ['Andheri', 'Bandra', 'Dadar', 'Thane', 'Borivali', 'Kurla', 'Ghatkopar', 'Vile Parle'],
  },
  'assignment-help-pune': {
    city: 'Pune',
    service: 'Assignment Writing',
    type: 'assignment',
    title: 'Assignment Writing Service in Pune | Engineering & College Help | AGO Innovations',
    description:
      'Get professional assignment writing help in Pune for engineering, medical, and B.Sc students. AGO Innovations delivers on time at affordable subscription prices.',
    keywords:
      'assignment writing service Pune, assignment help Pune, engineering assignment help Pune, college assignment Pune, student services Pune',
    canonical: 'https://agoinnovations.in/assignment-help-pune',
    areas: ['Shivajinagar', 'Kothrud', 'Hadapsar', 'Baner', 'Wakad', 'Pimpri', 'Chinchwad', 'Viman Nagar'],
  },
  'assignment-help-navi-mumbai': {
    city: 'Navi Mumbai',
    service: 'Assignment Writing',
    type: 'assignment',
    title: 'Assignment Writing Service in Navi Mumbai | Student Help | AGO Innovations',
    description:
      'Expert assignment writing service in Navi Mumbai for college and school students. Affordable plans, fast turnaround. Submit your requirement to AGO Innovations today.',
    keywords:
      'assignment writing Navi Mumbai, assignment help Navi Mumbai, student assignment service Navi Mumbai, college project help Navi Mumbai',
    canonical: 'https://agoinnovations.in/assignment-help-navi-mumbai',
    areas: ['Vashi', 'Nerul', 'Kharghar', 'Panvel', 'Airoli', 'Belapur', 'Sanpada', 'Turbhe'],
  },
  'assignment-help-thane': {
    city: 'Thane',
    service: 'Assignment Writing',
    type: 'assignment',
    title: 'Assignment Writing Service in Thane | College & School Help | AGO Innovations',
    description:
      'Assignment writing help for students in Thane. AGO Innovations covers engineering, school, and college assignments with fast and affordable services.',
    keywords:
      'assignment writing Thane, assignment help Thane, student services Thane, college assignment help Thane',
    canonical: 'https://agoinnovations.in/assignment-help-thane',
    areas: ['Thane West', 'Thane East', 'Kopri', 'Wagle Estate', 'Manpada', 'Ghodbunder Road'],
  },
  'assignment-help-kalyan': {
    city: 'Kalyan',
    service: 'Assignment Writing',
    type: 'assignment',
    title: 'Assignment Writing Service in Kalyan | Student Help | AGO Innovations',
    description:
      'Looking for assignment writing service in Kalyan? AGO Innovations offers affordable and expert assignment help for college and school students in Kalyan.',
    keywords:
      'assignment writing Kalyan, assignment help Kalyan, student services Kalyan, college assignment Kalyan',
    canonical: 'https://agoinnovations.in/assignment-help-kalyan',
    areas: ['Kalyan West', 'Kalyan East', 'Dombivli', 'Ulhasnagar', 'Ambarnath', 'Badlapur'],
  },
  // Insurance location pages
  'insurance-services-mumbai': {
    city: 'Mumbai',
    service: 'Insurance Advisory',
    type: 'insurance',
    title: 'Insurance Services in Mumbai | Life, Health & Vehicle Insurance | AGO Innovations',
    description:
      'Get trusted insurance advisory services in Mumbai. AGO Innovations helps you choose the best life insurance, health insurance, and vehicle insurance plans across Mumbai.',
    keywords:
      'insurance services Mumbai, life insurance Mumbai, health insurance plans Mumbai, car insurance Mumbai, insurance advisor Mumbai, insurance consultant Mumbai',
    canonical: 'https://agoinnovations.in/insurance-services-mumbai',
    areas: ['Andheri', 'Bandra', 'Borivali', 'Dadar', 'Kurla', 'Worli', 'Lower Parel', 'Navi Mumbai'],
  },
  'insurance-services-pune': {
    city: 'Pune',
    service: 'Insurance Advisory',
    type: 'insurance',
    title: 'Insurance Services in Pune | Life, Health & Vehicle Insurance | AGO Innovations',
    description:
      'Trusted insurance advisory in Pune. AGO Innovations guides you through life insurance, health plans, and investment options to secure your family\'s future.',
    keywords:
      'insurance services Pune, life insurance Pune, health insurance Pune, car insurance Pune, insurance advisor Pune, investment planning Pune',
    canonical: 'https://agoinnovations.in/insurance-services-pune',
    areas: ['Shivajinagar', 'Baner', 'Kothrud', 'Viman Nagar', 'Hadapsar', 'Pimpri', 'Chinchwad', 'Wakad'],
  },
};

const LocationPage = ({ slug }) => {
  const navigate = useNavigate();
  const config = LOCATION_CONFIG[slug];
  const isAssignment = config ? config.type === 'assignment' : false;

  useSEO({
    title: config ? config.title : 'Location Services | AGO Innovations',
    description: config ? config.description : 'AGO Innovations services.',
    keywords: config ? config.keywords : '',
    canonical: config ? config.canonical : undefined,
    structuredData: config
      ? {
          '@context': 'https://schema.org',
          '@type': 'Service',
          name: `${config.service} in ${config.city}`,
          provider: { '@type': 'Organization', name: 'AGO Innovations', url: 'https://agoinnovations.in' },
          areaServed: { '@type': 'City', name: config.city },
          description: config.description,
        }
      : undefined,
  });

  // Fallback if slug not found
  if (!config) {
    return (
      <div className="min-h-screen flex items-center justify-center text-gray-500 text-lg pt-24">
        Page not found.
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* HERO */}
      <section className="bg-gradient-to-br from-blue-900 via-blue-800 to-indigo-900 pt-28 pb-20 px-4 text-white text-center relative overflow-hidden">
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-yellow-400 via-orange-400 to-yellow-400" />
        <div className="absolute -top-20 -right-20 w-80 h-80 bg-blue-500/20 rounded-full blur-3xl" />

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          className="relative z-10 max-w-3xl mx-auto"
        >
          <div className="flex items-center justify-center gap-2 mb-5">
            <MapPin className="w-5 h-5 text-yellow-400" />
            <span className="bg-yellow-400/20 border border-yellow-400/40 text-yellow-300 text-sm font-semibold px-4 py-1 rounded-full">
              {config.city}, Maharashtra
            </span>
          </div>
          <h1 className="text-4xl md:text-5xl font-black mb-5 leading-tight">
            {config.service} Service
            <span className="block text-yellow-400">in {config.city}</span>
          </h1>
          <p className="text-blue-100 text-lg max-w-xl mx-auto mb-8 leading-relaxed">
            {isAssignment
              ? `Expert assignment writing help for students in ${config.city}. Fast, affordable, and plagiarism-free.`
              : `Trusted insurance advisory for individuals and families in ${config.city}. Life, health, and vehicle insurance guidance.`}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.97 }}
              onClick={() => navigate(isAssignment ? '/learn/customer-work' : '/finance')}
              className="bg-yellow-400 hover:bg-yellow-300 text-blue-900 font-bold px-8 py-4 rounded-full shadow-lg transition-colors flex items-center justify-center gap-2"
            >
              Get Started <ArrowRight className="w-5 h-5" />
            </motion.button>
            <a
              href="https://wa.me/91XXXXXXXXXX"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-green-500 hover:bg-green-400 text-white font-bold px-8 py-4 rounded-full shadow-lg transition-colors flex items-center justify-center gap-2"
            >
              <MessageCircle className="w-5 h-5" /> WhatsApp Us
            </a>
          </div>
        </motion.div>
      </section>

      {/* AREAS SERVED */}
      <section className="py-16 px-4 bg-gray-50">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            <h2 className="text-2xl md:text-3xl font-black text-gray-900 mb-8">
              Areas We Serve in {config.city}
            </h2>
            <div className="flex flex-wrap justify-center gap-3">
              {config.areas.map((area, i) => (
                <span
                  key={i}
                  className="bg-white border border-blue-200 text-blue-700 font-semibold text-sm px-5 py-2 rounded-full shadow-sm"
                >
                  📍 {area}
                </span>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* WHY AGO */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-4xl mx-auto">
          <motion.div
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="text-center mb-10"
          >
            <h2 className="text-2xl md:text-3xl font-black text-gray-900 mb-3">
              Why Students in {config.city} Choose AGO Innovations
            </h2>
          </motion.div>
          <div className="grid sm:grid-cols-2 gap-5">
            {(isAssignment ? [
              { icon: <BookOpen className="w-5 h-5" />, text: 'All streams — Engineering, Medical, B.Sc, School' },
              { icon: <Clock className="w-5 h-5" />, text: 'Fast delivery within 24–72 hours' },
              { icon: <Star className="w-5 h-5" />, text: 'Expert academic writers' },
              { icon: <CheckCircle className="w-5 h-5" />, text: 'Plagiarism-free, original content' },
              { icon: <Users className="w-5 h-5" />, text: 'Affordable subscription plans from ₹399' },
              { icon: <MessageCircle className="w-5 h-5" />, text: 'WhatsApp support for quick queries' },
            ] : [
              { icon: <CheckCircle className="w-5 h-5" />, text: 'Compare life, health and vehicle insurance plans' },
              { icon: <Star className="w-5 h-5" />, text: 'Trusted insurance advisors with local expertise' },
              { icon: <Users className="w-5 h-5" />, text: 'Personalized guidance for individuals and families' },
              { icon: <Clock className="w-5 h-5" />, text: 'Quick consultation and plan finalization' },
              { icon: <BookOpen className="w-5 h-5" />, text: 'Investment planning and mutual fund advisory' },
              { icon: <MessageCircle className="w-5 h-5" />, text: 'WhatsApp and email support available' },
            ]).map((item, i) => (
              <motion.div
                key={i}
                custom={i}
                variants={fadeUp}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                className="flex items-center gap-4 bg-blue-50 rounded-xl p-5 border border-blue-100"
              >
                <div className="w-10 h-10 bg-blue-600 rounded-xl flex items-center justify-center text-white flex-shrink-0">
                  {item.icon}
                </div>
                <span className="text-gray-800 font-medium text-sm">{item.text}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 px-4 bg-gradient-to-br from-blue-800 to-indigo-900 text-white text-center">
        <motion.div
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="max-w-2xl mx-auto"
        >
          <h2 className="text-2xl md:text-3xl font-black mb-4">
            {isAssignment
              ? `Get Assignment Help in ${config.city} Today`
              : `Get Insurance Advice in ${config.city}`}
          </h2>
          <p className="text-blue-200 mb-8">
            {isAssignment
              ? 'Subscribe to a plan and submit your first assignment requirement now.'
              : 'Connect with our advisors and find the right insurance plan for you.'}
          </p>
          <motion.button
            whileHover={{ scale: 1.05 }}
            onClick={() => navigate(isAssignment ? '/learn/customer-work' : '/finance')}
            className="bg-yellow-400 hover:bg-yellow-300 text-blue-900 font-bold px-10 py-4 rounded-full shadow-lg transition-colors"
          >
            {isAssignment ? 'Submit Requirement' : 'Explore Insurance Plans'}
          </motion.button>
        </motion.div>
      </section>
    </div>
  );
};

export default LocationPage;
